import { useMutation } from "@tanstack/react-query"
import { toast } from "sonner"
import { ReminderKeys } from "~/features/auth/api/keys"
import { apiClient } from "~/lib/client"

import { ErrorResponse, SuccessResponse } from "~/types"
import { handleError, handleResponse } from "~/utils/response"
import { MedicalFormData, MedicalFormUpdateData, MedicationDeliveryDetails, MedicationRefillPaymentDetails, SideEffectsDataSchema } from "../../schemas"
import { medicationsKeys } from "../queries/keys"
import { CreatePaymentForMedicationResponse, CreatePaymentForMedicationResponseData, GetNotificationReminderResponseData, GetRefillResponseData, RefillResponse, UpdateNotificationReminderData, UpdateNotificationSettingsResponse } from "../types"
import { getQueryClient } from "~/providers/query.provider"

export type CreateMedicalMedicationResponse = SuccessResponse<{
  message: string;
  success: string;
}> | ErrorResponse;

export const useCreateMedicalMedicationMutation = () => {
  const queryClient = getQueryClient()

  return useMutation<CreateMedicalMedicationResponse, unknown, MedicalFormData>({
    mutationFn: async (variables: MedicalFormData) => {
      try {
        const response = await apiClient("/medicals/medications", {
          method: "POST",
          body: JSON.stringify(variables),
        });

        const result = await handleResponse<CreateMedicalMedicationResponse>(response);

        if (!result.success) {
          throw new Error(result.error);
        }
        return result.data;
      } catch (error) {
        return handleError(error);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: medicationsKeys.medications() }),
  });
};

export type UpdateMedicalMedicationResponse = SuccessResponse<{
  message: string;
  success: string;
}> | ErrorResponse;

export const useUpdateMedicalMedicationMutation = () => {
  const queryClient = getQueryClient()

  return useMutation<UpdateMedicalMedicationResponse, unknown, MedicalFormUpdateData>({
    mutationFn: async (variables: MedicalFormUpdateData) => {
      try {
        const response = await apiClient(`/medicals/medications/${variables.id}`, {
          method: "PUT",
          body: JSON.stringify(variables),
        });

        const result = await handleResponse<UpdateMedicalMedicationResponse>(response);

        if (!result.success) {
          throw new Error(result.error);
        }
        return result.data;
      } catch (error) {
        return handleError(error);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: medicationsKeys.medications() }),
  });
};

export type DeleteMedicalMedicationResponse = SuccessResponse<{
  message: string;
  success: string;
}> | ErrorResponse;

export const useDeleteMedicalMedicationMutation = () => {
  const queryClient = getQueryClient()

  return useMutation<DeleteMedicalMedicationResponse, unknown, { id: string }>({
    mutationFn: async (variables: { id: string }) => {
      try {
        const response = await apiClient(`/medicals/medications/${variables.id}`, {
          method: "DELETE",
        });

        const result = await handleResponse<DeleteMedicalMedicationResponse>(response);

        if (!result.success) {
          throw new Error(result.error);
        }
        return result.data;
      } catch (error) {
        return handleError(error);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: medicationsKeys.medications() }),
  });
};


export const usePayForMedicationMutation = () => {
  const queryClient = getQueryClient()

  return useMutation<CreatePaymentForMedicationResponse, unknown, MedicationDeliveryDetails>({
    mutationFn: async (variables: MedicationDeliveryDetails) => {
      try {
        const response = await apiClient(`/medicals/medications/payment`, {
          method: "POST",
          body: JSON.stringify(variables),
        });

        return await handleResponse<CreatePaymentForMedicationResponseData>(response);
        ;
      } catch (error) {
        return handleError(error);
      }
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: medicationsKeys.prescriptions() });
    },
  });
};

export const usePayForRefillMedicationMutation = () => {
  const queryClient = getQueryClient()

  return useMutation<CreatePaymentForMedicationResponse, unknown, MedicationRefillPaymentDetails>({
    mutationFn: async (variables: MedicationRefillPaymentDetails) => {
      try {
        const response = await apiClient(`/medicals/refill/payment`, {
          method: "POST",
          body: JSON.stringify(variables),
        });

        return await handleResponse<CreatePaymentForMedicationResponseData>(response);
        ;
      } catch (error) {
        return handleError(error);
      }
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: medicationsKeys.prescriptions() });
    },
  });
};

export const useUpdateNotificationReminders = () => {
  const queryClient = getQueryClient()

  return useMutation<
    UpdateNotificationSettingsResponse,
    unknown,
    UpdateNotificationReminderData
  >({
    mutationFn: async (variables: UpdateNotificationReminderData) => {
      try {
        const response = await apiClient("/users/settings/reminder", {
          method: "PUT",
          body: JSON.stringify(variables),
        });


        return await handleResponse<GetNotificationReminderResponseData>(response);
      } catch (error) {
        return handleError(error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReminderKeys.settings() }
      );
      toast.success("Notification Reminder Successful!");
    },
  });
};


export const useMedicationRefillRequestMutation = (prescription_id: string, medication_id: string) => {
  const queryClient = getQueryClient()

  return useMutation<
    RefillResponse,
    unknown,
    SideEffectsDataSchema
  >({
    mutationFn: async (variables: SideEffectsDataSchema) => {
      try {
        const response = await apiClient(`/medicals/medications/refill/${prescription_id}/${medication_id}`, {
          method: "POST",
          body: JSON.stringify(variables),
        });


        return await handleResponse<GetRefillResponseData>(response);
      } catch (error) {
        return handleError(error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: medicationsKeys.refills(prescription_id, medication_id) }
      );
    },
  });
};
